import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";

import api from "../../../services/api";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import toastOptions from "../../../utils/toastOptions";
import { authenticationService } from "../../../services/auth.service";
import { displayDateTime, numberMask } from "../../../utils/masks";
import CurrencyInput from "../../../components/CurrencyInput";
import { getValidator } from "../../../utils/validatorsYup";
import Modal from "../../../components/modal";
import { PersonPaymentType } from "../../../models/enums/PersonPaymentType";
import IPersonCampaignDTO from "../../../models/IPersonCampaignDTO";
import moment from "../../../utils/momentConfig";
import IPersonBilling from "../../../models/IPersonBilling";

interface Params {
  personCampaign?: IPersonCampaignDTO;
  open: boolean;
  afterHandleSubmit: any;
  afterCloseModal: any;
}

export default function CampaignPayment(props: Params) {
  const { personCampaign, open, afterHandleSubmit, afterCloseModal } = props;

  const [modalIsOpen, setModelOpen] = useState(false);
  const [isReadOnly, setReadOnly] = useState<boolean>(true);  
  const [personPaid, setPersonPaid] = useState<IPersonBilling>();
  const [isAdding, setIsAdding] = useState<boolean>(true);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [lastPayment, setLastPayment] = useState<IPersonBilling>();

  const schema = yup.object().shape({
    paidAmount: getValidator({isNumeric: true}).required('Valor obrigatório'),
    paymentType: yup.string().required("Tipo obrigatório"),
    year: getValidator({isNumeric: true}).required('Ano obrigatório'),
    month: getValidator({isNumeric: true}).required('Mês obrigatório'),
    note: yup.string().max(250, (val) => `Máximo ${val.max} caracteres`)
  });

  const {
    register,
    handleSubmit,
    reset,
    errors,
    formState,
    setValue,
    clearErrors,
    trigger
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setReadOnly(authenticationService.isReadOnly());
    setModelOpen(open);

    if (!open) return;

    if (personCampaign) {
      const isAdding = (personCampaign?.idBilling ?? 0) === 0;
      
      setIsAdding(isAdding);

      const lastBill = (personCampaign as any).lastBilling;

      let nextPayment = moment();

      if (lastBill && lastBill.id > 0) {
        setLastPayment(lastBill);
        if (lastBill.month === 2) {
          nextPayment = moment(new Date(lastBill.year, 2, personCampaign.dueDay));
        } else {
          nextPayment = moment(new Date(lastBill.year, lastBill.month - 1, personCampaign.dueDay)).add(1, 'month');
        }
      }

      setPersonPaid({
        id: personCampaign.id,
        paidAt: new Date(),
        paidAmount: personCampaign.dueAmount,
        year: nextPayment.year(),
        month: nextPayment.month() + 1
      } as IPersonBilling);

      if (!isAdding && personCampaign?.idBilling) {
        handleEdit(personCampaign?.idBilling ?? 0);
      } else {
        setIsDisabled(false);
      }
    }

  }, [personCampaign, open]);

  const handleEdit = async (id: number) => {
    try {
      setIsDisabled(true);

      const { data } = await api.get(`/campaigns/payments/${id}`);

      setPersonPaid(data.item);

      await setValue('year', data.item.year);
      await setValue('month', data.item.month);
      await setValue('paymentType', data.item.paymentType);

    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.warning(m, toastOptions);
      });
    } finally {
      setIsDisabled(false);
    }
  };

  const closeModal = () => {
    handleCloseModal();
    reset();
    afterCloseModal();
  };

  const handleCloseModal = () => {
    clearErrors();
    setModelOpen(false);
    afterCloseModal();
  };

  const onSubmit = async (data: IPersonBilling) => {
    try {
      if (personPaid) {
        data.idPerson = personPaid.id;
        data.paidAt = personPaid.paidAt;

        if (personCampaign && personCampaign.idBilling) data.id = personCampaign?.idBilling;
        if (personCampaign && personCampaign.idPerson) data.idPerson = personCampaign?.idPerson;

        if (isAdding) {
          data.id = personPaid.id;
          await api.post(`/campaigns/person/${personPaid.id}/payment`, data);
          toast.success("Pagamento realizado com sucesso!", toastOptions);
        } else {
          await api.put(`/campaigns/payments/${personPaid.id}`, data);
          toast.success("Pagamento atualizado com sucesso!", toastOptions);
        }

        handleCloseModal();
        afterHandleSubmit(personPaid.paidAt);
      }
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    }
  };

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        className="modal-small"
        shouldCloseOnOverlayClick={false}
      >
        <div className="uk-modal-header">
          <span className="uk-modal-title uk-text-large">{!isAdding && 'Editar'} Pagamento Doação Campanha</span>
        </div>
        <button
          className="uk-modal-close-default"
          uk-close="true"
          type="button"
          onClick={handleCloseModal}
        />
        <form onReset={reset}
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
            return handleSubmit<IPersonBilling>(onSubmit)(e)
          }}
        >
          <div className="uk-modal-body">
            <div className="uk-grid-small" uk-grid="true">
              <div className="uk-width-1-1">
                <span className="uk-form-label uk-text-light uk-text-large">
                  {personCampaign?.name}
                </span>
                {(lastPayment && lastPayment.id > 0) && <>
                  <br />
                  <span className="uk-form-label uk-text-light uk-text-italic">
                    Último pagamento registrado em {displayDateTime(lastPayment.paidAt)} referente à {lastPayment.month}/{lastPayment.year}
                  </span>
                </>}
              </div>
              <div className="uk-width-1-6@s">
                <label className="uk-form-label" htmlFor="year">Ano</label>
                <input type="number" 
                  id="year" 
                  name="year" 
                  placeholder="Ano" 
                  className="uk-input uk-form-small" 
                  defaultValue={personPaid?.year}
                  ref={register} 
                  onChange={(e) => { e.target.value = numberMask(e.target.value) }}
                  disabled={isDisabled}
                />
                <small className="uk-text-danger">{errors.year?.message}</small>
              </div>
              <div className="uk-width-1-6@s">
                <div className="uk-form-controls">
                  <label className="uk-form-label" htmlFor="month">Mês</label>
                  <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                    <select className="uk-form-small"
                      id="month"
                      name="month"                      
                      defaultValue={personPaid?.month}
                      ref={register}
                      onChange={(e) => {
                        setPersonPaid({
                          ...personPaid,
                          month: +e.target.value
                        } as IPersonBilling);
                        setValue('month', e.target.value);
                      }}
                    >
                      {
                        moment.months().map((m, i) => {
                          return <option key={`month${i+1}`} value={i+1}>{m.charAt(0).toUpperCase() + m.slice(1).toLowerCase()}</option>
                        })
                      }
                    </select>
                    <button
                      className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                      style={{textTransform: 'none'}} 
                      type="button" 
                      tabIndex={-1}
                      disabled={isDisabled}
                    >
                      <span>{moment.months()[(personPaid?.month ?? 0) - 1]}</span>
                      <span uk-icon="icon: chevron-down"></span>
                    </button>
                  </div>
                </div>
                <small className="uk-text-danger">{errors.month?.message}</small>
              </div>
              <div className="uk-width-1-3@s">
                <CurrencyInput
                  name="paidAmount"
                  label="Valor"
                  placeholder="Valor"
                  defaultValue={personPaid?.paidAmount}
                  register={register}
                  setValue={setValue}
                  disabled={isDisabled}
                  trigger={trigger}
                  onChange={(value) => setPersonPaid({...personPaid, paidAmount: value} as IPersonBilling)}
                  error={errors.paidAmount?.message}
                />
              </div>              
              <div className="uk-width-1-3@s">
                <div className="uk-form-controls">
                  <label className="uk-form-label" htmlFor="paymentType">Pago via</label>
                  <div
                    uk-form-custom="target: > * > span:first-child"
                    className="uk-width-1"
                  >
                    <select
                      className="uk-form-small"
                      id="paymentType"
                      name="paymentType"
                      defaultValue={personCampaign?.paymentType}
                      ref={register}
                      onChange={(e) => {
                        setValue('paymentType', e.target.value);
                      }}
                    >
                      <option value="">Selecione...</option>
                      {
                        Object.keys(PersonPaymentType)
                          .map((key) => {
                            return (
                              <option key={key} value={PersonPaymentType[key as keyof typeof PersonPaymentType]}>
                                {PersonPaymentType[key as keyof typeof PersonPaymentType]}
                              </option>
                            );
                          })
                      }
                    </select>
                    <button
                      className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                      style={{ textTransform: "none" }}
                      type="button"
                      tabIndex={-1}
                      disabled={isDisabled}
                    >
                      <span>{personPaid?.paymentType as PersonPaymentType}</span>
                      <span uk-icon="icon: chevron-down"></span>
                    </button>
                  </div>
                </div>
                <small className="uk-text-danger">{errors.paymentType?.message}</small>
              </div>
              <div className="uk-width-1@s">
                <label className="uk-form-label" htmlFor="note">Observações</label>
                <textarea
                  id="note"
                  name="note"
                  className="uk-textarea uk-form-small"
                  rows={3}
                  style={{ resize: "none", textTransform: "uppercase" }}
                  defaultValue={personPaid?.note}
                  autoComplete={"off"}
                  ref={register}
                  disabled={isDisabled}
                />
                <small className="uk-text-danger">{errors.note?.message}</small>
              </div>
              <div className="uk-width-1@s">
                {personPaid?.id && personPaid?.createdBy && (
                  <div className="uk-grid-small" uk-grid="true">
                    <div className="uk-width-1-2@s">
                      <span className="uk-text-italic uk-text-small">
                        Adicionado por:
                      </span>
                      <span className="uk-text-muted uk-text-small uk-margin-small-left">
                        {`${personPaid?.createdBy} - ${displayDateTime(personPaid?.paidAt)}`}
                      </span>
                    </div>
                  </div>
                )}                 
              </div>
            </div>
          </div>
          <div className="uk-modal-footer">
            <div
              className="uk-grid-small uk-child-width-1-4@s uk-flex-center uk-text-center"
              uk-grid="true"
            >
              <button
                className="uk-button uk-button-default uk-button-small uk-modal-close uk-margin-left"
                onClick={handleCloseModal}
                type="button"
              >
                Cancelar
              </button>
              <button
                disabled={formState.isSubmitting || isReadOnly || isDisabled}
                className="uk-button uk-button-primary uk-button-small uk-margin-left"
                type="submit"
              >
                {formState.isSubmitting && <div uk-spinner="true; ratio:0.5"></div>}
                Salvar
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </>
  );
}
